import type { RunMapStop } from '../runTypes';
import { RUN_ROUTE_COLOR, RUN_ROUTE_ACTIVE_COLOR, RUN_STOP_COLORS } from '../runTypes';
import { decodePolyline } from '@utils/decodePolyline';

// TYPES --------------------------------------------------------------------- //

interface CreateRunRouteOverlaysOptions {
  map: google.maps.Map;
  stops: RunMapStop[];
  /** Encoded Google polylines for each leg (index i = leg from stop i to stop i+1) */
  legPolylines?: (string | null)[];
  /** Index of the selected stop — highlights inbound + outbound legs */
  selectedStopIndex?: number | null;
  prefersReducedMotion: boolean;
}

interface RunRouteOverlayResult {
  polylines: google.maps.Polyline[];
  animatedPolyline: google.maps.Polyline | null;
  animationFrameIds: number[];
}

// FACTORY ------------------------------------------------------------------- //

/**
 * Creates route polylines connecting stops in sequence.
 * Uses encoded Google polylines from lane data when available,
 * falling back to straight lines between stops.
 * Each leg gets a glow + main polyline. The active leg gets animated dashes.
 */
export function createRunRouteOverlays({
  map,
  stops,
  legPolylines,
  selectedStopIndex,
  prefersReducedMotion,
}: CreateRunRouteOverlaysOptions): RunRouteOverlayResult {
  const polylines: google.maps.Polyline[] = [];
  const animationFrameIds: number[] = [];
  const animatedPolyline: google.maps.Polyline | null = null;

  if (stops.length < 2) return { polylines, animatedPolyline, animationFrameIds };

  for (let i = 0; i < stops.length - 1; i++) {
    const from = stops[i];
    const to = stops[i + 1];

    if (from.lat == null || from.lng == null || to.lat == null || to.lng == null) continue;

    // Try to use encoded polyline from lane data
    const encodedPolyline = legPolylines?.[i];
    let path: google.maps.LatLngLiteral[];

    if (encodedPolyline) {
      path = decodePolyline(encodedPolyline);
    } else {
      // Fallback: straight line
      path = [
        { lat: from.lat, lng: from.lng },
        { lat: to.lat, lng: to.lng },
      ];
    }

    if (path.length < 2) continue;

    // Determine leg color based on phases (selection modifies weight/glow, not color)
    const isCompletedLeg = from.phase === 'completed' && to.phase === 'completed';
    const isActiveLeg = from.phase === 'active' || to.phase === 'active';
    const isSelectedLeg = selectedStopIndex != null && (i === selectedStopIndex || i === selectedStopIndex - 1);
    const isDimmed = selectedStopIndex != null && !isSelectedLeg;

    const color = isCompletedLeg
      ? RUN_STOP_COLORS.completed
      : isActiveLeg
        ? RUN_ROUTE_ACTIVE_COLOR
        : RUN_ROUTE_COLOR;

    const opacity = isDimmed ? 0.25 : isCompletedLeg ? 0.6 : 1;
    const weight = isSelectedLeg ? 6 : 4;
    const glowWeight = isSelectedLeg ? 14 : 10;

    // Glow polyline
    polylines.push(
      new google.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: color,
        strokeWeight: glowWeight,
        strokeOpacity: isSelectedLeg ? 0.25 : 0.15,
        zIndex: isSelectedLeg ? 8 : 3,
        clickable: false,
        map,
      }),
    );

    // Main polyline
    polylines.push(
      new google.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: color,
        strokeWeight: weight,
        strokeOpacity: opacity,
        zIndex: isSelectedLeg ? 9 : 4,
        clickable: false,
        map,
      }),
    );

    // Animated directional dashes on active or selected legs only
    if (isActiveLeg || isSelectedLeg) {
      const DASH_REPEAT_PX = 24;
      const dashColor = isSelectedLeg ? '#ffffff' : '#93c5fd';
      const dashOpacity = isDimmed ? 0.15 : isSelectedLeg ? 0.8 : 0.45;
      const speed = isActiveLeg ? 0.3 : isSelectedLeg ? 0.25 : 0.15;

      const lineSymbol: google.maps.Symbol = {
        path: 'M 0,-1 0,1',
        strokeOpacity: dashOpacity,
        strokeColor: dashColor,
        strokeWeight: 4,
        scale: 3,
      };

      const anim = new google.maps.Polyline({
        path,
        strokeOpacity: 0,
        strokeWeight: weight,
        icons: [{ icon: lineSymbol, offset: '0', repeat: `${DASH_REPEAT_PX}px` }],
        zIndex: isSelectedLeg ? 10 : 5,
        clickable: false,
        map,
      });

      polylines.push(anim);

      if (!prefersReducedMotion) {
        let legOffset = 0;
        const animate = () => {
          legOffset = (legOffset + speed) % DASH_REPEAT_PX;
          anim.set('icons', [{ icon: { ...lineSymbol }, offset: `${legOffset}px`, repeat: `${DASH_REPEAT_PX}px` }]);
          const frameId = requestAnimationFrame(animate);
          animationFrameIds.push(frameId);
        };
        const firstFrameId = requestAnimationFrame(animate);
        animationFrameIds.push(firstFrameId);
      }
    }
  }

  return { polylines, animatedPolyline, animationFrameIds };
}
